import React, { useState, useRef, useEffect, useCallback } from 'react';
import { agent } from '../services/api.js';
import './UnderwriterChat.css';

export function UnderwriterChat({ submissionId, isOpen: isOpenProp, onToggle }) {
  const [isOpenInternal, setIsOpenInternal] = useState(false);
  const isControlled = onToggle != null;
  const isOpen = isControlled ? isOpenProp : isOpenInternal;
  const handleToggle = isControlled ? onToggle : () => setIsOpenInternal((p) => !p);

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => `chat-${Date.now()}`);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    const userMsg = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const payload = {
        message: text,
        sessionId,
      };
      if (submissionId) payload.submissionId = submissionId;

      const result = await agent.chat(payload);
      const data = result.data || result;

      const assistantMsg = {
        role: 'assistant',
        content: data.response || 'No response received.',
        timestamp: data.timestamp || new Date().toISOString(),
      };
      setMessages((prev) => [...prev, assistantMsg]);
    } catch (err) {
      const errorMsg = {
        role: 'assistant',
        content: 'Sorry, I was unable to process that request. Please try again.',
        timestamp: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      {/* Floating toggle button */}
      <button
        className="uw-chat__toggle"
        onClick={handleToggle}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
        title="DEX Underwriter Assistant"
      >
        {isOpen ? (
          <span className="uw-chat__toggle-icon">✕</span>
        ) : (
          <>
            <span className="uw-chat__toggle-icon">🤖</span>
            <span className="uw-chat__toggle-label">Ask DEX</span>
          </>
        )}
      </button>

      {/* Chat panel */}
      <div className={`uw-chat ${isOpen ? 'uw-chat--open' : ''}`}>
        <div className="uw-chat__header">
          <h3 className="uw-chat__title">DEX Underwriter</h3>
          <button
            className="uw-chat__close"
            onClick={handleToggle}
            aria-label="Close chat"
          >
            ✕
          </button>
        </div>

        <div className="uw-chat__messages">
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`uw-chat__message uw-chat__message--${msg.role}`}
            >
              <div className="uw-chat__bubble">
                <p className="uw-chat__content">{msg.content}</p>
              </div>
            </div>
          ))}
          {loading && (
            <div className="uw-chat__message uw-chat__message--assistant">
              <div className="uw-chat__bubble uw-chat__bubble--loading">
                <span className="uw-chat__typing-dot" />
                <span className="uw-chat__typing-dot" />
                <span className="uw-chat__typing-dot" />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="uw-chat__input-area">
          <input
            type="text"
            className="uw-chat__input"
            placeholder={
              submissionId
                ? `Ask about submission ${submissionId}...`
                : 'Ask about risk scoring, guidelines...'
            }
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={loading}
          />
          <button
            className="uw-chat__send btn btn--primary"
            onClick={handleSend}
            disabled={!input.trim() || loading}
          >
            Send
          </button>
        </div>
      </div>
    </>
  );
}
